import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getPartnerUsersByPartnerId, getAllPartners} from '../../services/PartnerUserService';
import { deletePartnerUser } from "../../services/PartnerUserService";
import { getUsers } from "../../services/UserInfo";
import { PartnerUser } from '../../data/PartnerUserData';
import {
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Chip,
    Box,
    IconButton,
    Typography
} from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { Done as DoneIcon, Clear as ClearIcon } from "@mui/icons-material";
import PartnerUserDialog from "./PartnerUserDialog";
import DeletePartnerUserDialog from "./DeletePartnerUserDialog";
import EditPartnerUserDialog from "./EditPartnerUserDialog";

interface PartnerUserWithName extends PartnerUser {
    UserName?: string;
    Email?: string;
    Biography?: string;
}

const PartnerUsers = () => {
    const { partnerId } = useParams<{ partnerId: string }>();
    const [partnerName, setPartnerName] = useState<string>("Loading...");
    const [users, setUsers] = useState<PartnerUserWithName[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    // Edit dialog state
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedPartnerUserId, setSelectedPartnerUserId] = useState<string>("");
    const [selectedUserName, setSelectedUserName] = useState<string>("Unknown User");
    const [selectedSponsorAdmin, setSelectedSponsorAdmin] = useState<boolean>(false);


    const fetchPartnerName = async () => {
        try {
            if (partnerId) {
                console.log("Fetching all partners...");
                const allPartners = await getAllPartners();
                console.log("All Partners Response:", allPartners);

                if (Array.isArray(allPartners) && allPartners.length > 0) {
                    const partner = allPartners.find((p: any) => p.PartnerId?.toLowerCase() === partnerId.toLowerCase());
                    if (partner) {
                        console.log("Found Partner:", partner);
                        setPartnerName(partner.Name || "Unknown Partner");
                    } else {
                        console.warn(`No partner found with ID: ${partnerId}`);
                        setPartnerName("Unknown Partner");
                    }
                } else {
                    console.warn("No partners returned from the API.");
                    setPartnerName("Unknown Partner");
                }
            } else {
                console.error("Missing partner ID.");
                setError("Missing partner ID.");
            }
        } catch (err: any) {
            console.error("Error fetching partner name:", err);
            setPartnerName("Unknown Partner");
        } finally {
            setLoading(false);
        }
    };

    const fetchUsersAndMapNames = async () => {
        try {
            if (partnerId) {
                const partnerUsers = await getPartnerUsersByPartnerId(partnerId);
                console.log("Found users:", partnerUsers);

                const allUsers = await getUsers();

                const mappedUsers = partnerUsers.map((partnerUser) => {
                    const userInfo = allUsers.find(user => user.Id === partnerUser.UserID);
                    return {
                        ...partnerUser,
                        UserName: userInfo ? `${userInfo.FirstName} ${userInfo.LastName}` : "Unknown User",
                        Email: userInfo?.Email ?? "No Email",
                        Biography: userInfo?.Biography ?? "No Biography"
                    };
                });

                setUsers(mappedUsers);
            } else {
                setError("Missing partner ID.");
            }
        } catch (err: any) {
            console.error("Error fetching partner users or mapping names:", err);
            setError(err.message);
            //setUsers([]); // Safely set an empty list to avoid breaking the UI
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPartnerName();
        fetchUsersAndMapNames();
    }, [partnerId]);

    const handleAddPartnerUser = () => {
        console.log("Add Partner User button clicked");
        setIsDialogOpen(true);
    };

    const handleSaveUser = async () => {
        setIsEditDialogOpen(false);
        await fetchUsersAndMapNames(); // Reload complete list with updated data
    };

    const handleEditUser = (userId: string) => {
        console.log(`Edit User button clicked for User ID: ${userId}`);
        const userToEdit = users.find(user => user.PartnerUserID === userId);
        if (userToEdit) {
            setSelectedPartnerUserId(userId);
            setSelectedUserName(userToEdit.UserName || "Unknown User");
            setSelectedSponsorAdmin(userToEdit.SponsorAdmin);
            setIsEditDialogOpen(true);
        }
    };

    const handleDeleteUser = (partnerUserId: string, userName: string) => {
        console.log(`Delete User button clicked for Partner User ID: ${partnerUserId}`);
        setSelectedPartnerUserId(partnerUserId);
        setSelectedUserName(userName);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteSuccess = async () => {
        setIsDeleteDialogOpen(false); // Close the dialog first

        try {
            await deletePartnerUser(partnerId!, selectedPartnerUserId);

            // Optimistically remove the deleted user from the list **before** setting loading state
            setUsers(prevUsers => prevUsers.filter(user => user.PartnerUserID !== selectedPartnerUserId));

        } catch (error) {
            console.error("Error deleting user:", error);
        }
    };

    if (loading) return <CircularProgress />;
    if (error) return <div>Error: {error}</div>;

    return (
        <div style={{ padding: '50px'}}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                marginBottom="20px"
            >
                <h2 style={{ margin: 0, fontWeight: 'bold' }}>
                    {partnerName}
                </h2>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddPartnerUser}
                >
                    Add User
                </Button>
            </Box>

            <PartnerUserDialog
                isOpen={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                partnerId={partnerId!}
                existingPartnerUsers={users.map(user => user.UserID)}
                onSave={handleSaveUser}
            />

            <DeletePartnerUserDialog
                isOpen={isDeleteDialogOpen}
                onClose={() => setIsDeleteDialogOpen(false)}
                partnerId={partnerId!}
                partnerUserId={selectedPartnerUserId}
                userName={selectedUserName}
                onDeleteSuccess={handleDeleteSuccess}
            />

            <EditPartnerUserDialog
                isOpen={isEditDialogOpen}
                onClose={() => setIsEditDialogOpen(false)}
                partnerId={partnerId!}
                partnerUserId={selectedPartnerUserId}
                userName={selectedUserName}
                isSponsorAdmin={selectedSponsorAdmin}
                onSave={handleSaveUser}
            />

            <TableContainer component={Paper} style={{ boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}>
                <Table>
                    <TableHead style={{ backgroundColor: '#f0f0f0' }}>
                        <TableRow>
                            {['Name', 'Email', 'Role', 'Actions'].map((header) => (
                                <TableCell
                                    key={header}
                                    align={header === 'Actions' ? 'right' : header === 'Role' ? 'center' : 'left'}
                                >
                                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'black' }}>
                                        {header}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {users.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    <Typography variant="h6" style={{ fontWeight: 'bold', color: 'gray' }}>
                                        No users found. Please add a new user.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            users.map((user) => (
                            <TableRow key={user.PartnerUserID}>
                                <TableCell><Typography variant="h6" style={{ fontWeight: 'bold', color: 'black' }}>{user.UserName}</Typography></TableCell>
                                <TableCell><Typography variant="h6" style={{ fontWeight: 'bold', color: 'black' }}>{user.Email}</Typography></TableCell>
                                <TableCell align="center">
                                    <Chip
                                        icon={user.SponsorAdmin ? <DoneIcon style={{ color: 'white' }} /> : <ClearIcon style={{ color: 'white' }} />}
                                        label={
                                            <Typography style={{ color: 'white' }}>
                                                {user.SponsorAdmin ? "Admin" : "Not an Admin"}
                                            </Typography>
                                        }
                                        style={{ backgroundColor: user.SponsorAdmin ? "green" : "red" }}
                                    />
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton edge="end" aria-label="edit" onClick={() => handleEditUser(user.PartnerUserID)}>
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteUser(user.PartnerUserID, user.UserName!)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};

export default PartnerUsers;